import { useState, useCallback, useMemo } from 'react';
import {
  ScrollText,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Loader2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { UserRole } from '@pms/shared';
import { useAuditLogs } from '@/hooks/api/useAudit';
import { useAuth } from '@/hooks/useAuth';
import { AccessDenied } from '@/components/ui/AccessDenied';
import type { AuditLogEntry, AuditLogsQuery } from '@/services/api/audit.api';

// ─── Constants ──────────────────────────────────────────────────────────────────

const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'All Entities' },
  { value: 'Booking', label: 'Booking' },
  { value: 'Guest', label: 'Guest' },
  { value: 'Property', label: 'Property' },
  { value: 'Unit', label: 'Unit' },
  { value: 'GroupBooking', label: 'Group Booking' },
] as const;

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'check_in', label: 'Check In' },
  { value: 'check_out', label: 'Check Out' },
  { value: 'cancel', label: 'Cancel' },
] as const;

const ACTION_BADGE_STYLES: Record<string, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  check_in: 'bg-teal-100 text-teal-800',
  check_out: 'bg-purple-100 text-purple-800',
  cancel: 'bg-orange-100 text-orange-800',
};

const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  check_in: 'Check In',
  check_out: 'Check Out',
  cancel: 'Cancel',
};

const PAGE_SIZE = 20;

// ─── Helpers ────────────────────────────────────────────────────────────────────

function fmtTimestamp(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy HH:mm:ss');
  } catch {
    return iso;
  }
}

function ActionBadge({ action }: { action: string }) {
  const style = ACTION_BADGE_STYLES[action] ?? 'bg-gray-100 text-gray-700';
  const label = ACTION_LABELS[action] ?? action;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', style)}>
      {label}
    </span>
  );
}

// ─── Changes Detail ─────────────────────────────────────────────────────────────

function ChangesDetail({ changes }: { changes: Record<string, { old: unknown; new: unknown }> }) {
  const entries = Object.entries(changes);
  if (entries.length === 0) {
    return <p className="text-xs text-gray-400 italic">No details</p>;
  }

  return (
    <div className="text-xs space-y-1">
      {entries.map(([field, { old: oldVal, new: newVal }]) => (
        <div key={field} className="flex items-baseline gap-1 flex-wrap">
          <span className="font-medium text-gray-700">{field}:</span>
          <span className="text-red-500 line-through">{String(oldVal ?? 'null')}</span>
          <span className="text-gray-400" aria-hidden="true">&rarr;</span>
          <span className="text-green-600">{String(newVal ?? 'null')}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Audit Row ──────────────────────────────────────────────────────────────────

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasChanges = entry.changes && Object.keys(entry.changes).length > 0;

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
          {fmtTimestamp(entry.createdAt)}
        </td>
        <td className="px-4 py-3 text-sm text-gray-900">
          {entry.actorEmail ?? <span className="text-gray-400 italic">System</span>}
        </td>
        <td className="px-4 py-3">
          <ActionBadge action={entry.action} />
        </td>
        <td className="px-4 py-3 text-sm text-gray-700">
          <span className="font-medium">{entry.entityType}</span>
          {entry.entityName && (
            <>
              <span className="text-gray-400 mx-1" aria-hidden="true">&mdash;</span>
              <span>{entry.entityName}</span>
            </>
          )}
        </td>
        <td className="px-4 py-3">
          {hasChanges ? (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 transition-colors"
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Hide' : 'Show'} changes for ${entry.entityType} ${entry.entityName ?? entry.entityId}`}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {expanded ? 'Hide' : 'Show'} Changes
            </button>
          ) : (
            <span className="text-xs text-gray-400">&mdash;</span>
          )}
        </td>
      </tr>
      {expanded && hasChanges && entry.changes && (
        <tr>
          <td colSpan={5} className="px-4 py-3 bg-gray-50 border-t border-gray-100">
            <ChangesDetail changes={entry.changes} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Filter Bar ─────────────────────────────────────────────────────────────────

interface FilterBarProps {
  entityType: string;
  action: string;
  from: string;
  to: string;
  onEntityTypeChange: (value: string) => void;
  onActionChange: (value: string) => void;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
}

function FilterBar({
  entityType,
  action,
  from,
  to,
  onEntityTypeChange,
  onActionChange,
  onFromChange,
  onToChange,
}: FilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div>
        <label htmlFor="audit-entity-type" className="sr-only">Entity Type</label>
        <select
          id="audit-entity-type"
          value={entityType}
          onChange={(e) => onEntityTypeChange(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {ENTITY_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="audit-action" className="sr-only">Action</label>
        <select
          id="audit-action"
          value={action}
          onChange={(e) => onActionChange(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <label htmlFor="audit-from" className="sr-only">From date</label>
        <input
          id="audit-from"
          type="date"
          value={from}
          onChange={(e) => onFromChange(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          aria-label="Filter from date"
        />
        <span className="text-gray-400 text-sm">to</span>
        <label htmlFor="audit-to" className="sr-only">To date</label>
        <input
          id="audit-to"
          type="date"
          value={to}
          onChange={(e) => onToChange(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          aria-label="Filter to date"
        />
      </div>
    </div>
  );
}

// ─── Pagination ─────────────────────────────────────────────────────────────────

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function Pagination({ page, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <nav className="flex items-center justify-between px-4 py-3" aria-label="Audit log pagination">
      <p className="text-sm text-gray-500">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft size={14} />
          Previous
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Next page"
        >
          Next
          <ChevronRight size={14} />
        </button>
      </div>
    </nav>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

export function AuditLogPage() {
  const { user } = useAuth();
  const canAccess = user?.role === UserRole.ADMIN;
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const query = useMemo<AuditLogsQuery>(() => {
    const q: AuditLogsQuery = { page, limit: PAGE_SIZE };
    if (entityType) q.entityType = entityType;
    if (action) q.action = action;
    if (from) q.from = from;
    if (to) q.to = to;
    return q;
  }, [page, entityType, action, from, to]);

  const { data, isLoading } = useAuditLogs(query, canAccess);

  const entries: AuditLogEntry[] = data?.data ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;

  const handleEntityTypeChange = useCallback((value: string) => {
    setEntityType(value);
    setPage(1);
  }, []);

  const handleActionChange = useCallback((value: string) => {
    setAction(value);
    setPage(1);
  }, []);

  const handleFromChange = useCallback((value: string) => {
    setFrom(value);
    setPage(1);
  }, []);

  const handleToChange = useCallback((value: string) => {
    setTo(value);
    setPage(1);
  }, []);

  if (!canAccess) {
    return <AccessDenied message="The audit log is restricted to Admins." />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg bg-gray-100" aria-hidden="true">
            <ScrollText size={20} className="text-gray-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Activity Log</h1>
            <p className="text-sm text-gray-500">System-wide audit trail</p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <FilterBar
          entityType={entityType}
          action={action}
          from={from}
          to={to}
          onEntityTypeChange={handleEntityTypeChange}
          onActionChange={handleActionChange}
          onFromChange={handleFromChange}
          onToChange={handleToChange}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-primary-600" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
              <ScrollText size={24} className="text-gray-400" aria-hidden="true" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">No audit logs found</h3>
            <p className="text-sm text-gray-500 max-w-xs">
              Adjust your filters or check back later.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Timestamp
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Actor
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Action
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Entity
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Changes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {entries.map((entry) => (
                    <AuditRow key={entry.id} entry={entry} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-gray-200">
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
